import { useCallback, useEffect, useRef, useState } from "react";
import { useMap, FeatureGroup } from "react-leaflet";
import L, { type LayersControlEvent } from "leaflet";
import type { RequestedPicturesPlotDataResponse } from "../../types";
import { useRequestedPicturesPlot } from "@/features/map/hooks/useRequestedPicturesPlot";
import { toast } from "react-toastify";
import { type PropertySelectionRow } from "@/features/map/types";
import type { BookTypeEng } from "@/types/acquirebook";
import { findExistingRow } from "@/features/map/utils/requestedPicturesPlot/findExistingRow";
import { selectExistingRow } from "@/features/map/utils/propertySelection/selectExistingRow";
import { addNewRowAndSelect } from "@/features/map/utils/requestedPicturesPlot/addNewRowAndSelect";
import {
  generatePlotId,
  cleanupObsoleteMarkers,
} from "../../utils/requestedPicturesPlot/markerManager";
import { useFeatureFlags } from "@/configs/featureFlag";
import { RealEstateDetailDialog } from "../requestedPictures/RealEstateDetailDialog";
import { useAdditionalFieldsConfig } from "../../hooks/useAdditionalFieldsConfig";
import { CanvasPinMarker } from "../../utils/canvasPin/CanvasPinMarker";
import type { CanvasPinRenderer } from "../../utils/canvasPin/CanvasPinRenderer";
import { getSharedCanvasPinRenderer } from "../../utils/canvasPin/sharedCanvasPinRenderer";
import {
  PIN_COLORS,
  getTeardropPinIcon,
} from "../../utils/canvasPin/teardropPin";

interface RequestedPicturesPlotLayerProps {
  defaultVisible: boolean;
  handlePinSave?: () => void;
  setSelectedProperties?: React.Dispatch<
    React.SetStateAction<PropertySelectionRow[]>
  >;
  selectedProperties?: PropertySelectionRow[];
  checkDuplicateProperties?: (
    rows: PropertySelectionRow[],
    bookType: BookTypeEng,
    prefName: string,
    location: string,
    chiban: string,
    area: number | null
  ) => boolean;
  onTagsChange?: (
    tags: string[],
    prefName: string,
    location: string,
    chiban: string
  ) => void;
  onRemarksChange?: (
    remarks: string,
    prefName: string,
    location: string,
    chiban: string
  ) => void;
  onAdditionalFieldsChange?: (
    additionalFields: string,
    prefName: string,
    location: string,
    chiban: string
  ) => void;
  setCurrentPopupRowId?: React.Dispatch<React.SetStateAction<number | null>>;
  visible?: boolean;
}

export const REQUESTED_PICTURES_PLOT_LAYER_NAME =
  "登記解析済み登記情報を表示する(土地)";

// 表示するズーム（zoomLevel - 1 と比べる）。レイヤー一覧の表記（overlayGroups の minZoom: 14）は +1 した値
const MIN_ZOOM = 13;
const MAX_ZOOM = 20;

export const RequestedPicturesPlotLayer: React.FC<
  RequestedPicturesPlotLayerProps
> = ({
  defaultVisible,
  handlePinSave: parentHandlePinSave,
  setSelectedProperties,
  selectedProperties = [],
  checkDuplicateProperties,
  onTagsChange,
  onRemarksChange,
  onAdditionalFieldsChange,
  setCurrentPopupRowId,
  visible: visibleProp,
}) => {
  const map = useMap();
  const layerRef = useRef<L.LayerGroup | null>(null);
  const markersRef = useRef<Map<string, CanvasPinMarker>>(new Map());
  const toastShownRef = useRef<boolean>(false);
  const [visible, setVisible] = useState(defaultVisible);
  const [zoomLevel, setZoomLevel] = useState<number>(map.getZoom());
  const [selectedPlot, setSelectedPlot] =
    useState<RequestedPicturesPlotDataResponse | null>(null);
  const selectedPlotRef = useRef<RequestedPicturesPlotDataResponse | null>(
    null
  );

  const { crm } = useFeatureFlags();
  const { availableFields, hasAvailableFields } = useAdditionalFieldsConfig();

  // 最新のselectedPropertiesを常に参照できるようにする
  // useEffectの依存配列にselectedPropertiesを入れるとマーカーが頻繁に再作成されるため、
  // Refを使用して最新の値を参照しつつ、依存関係は回避する
  const selectedPropertiesRef = useRef(selectedProperties);
  selectedPropertiesRef.current = selectedProperties;

  const { requestedPictures, isLoading, isError } =
    useRequestedPicturesPlot(visible);

  const handlePinSave = useCallback((): void => {
    if (parentHandlePinSave) {
      parentHandlePinSave();
    }
  }, [parentHandlePinSave]);

  // マーカークリック時にPropertySelectionGridに追加する関数
  const handleMarkerClick = useCallback(
    (plot: RequestedPicturesPlotDataResponse): void => {
      if (!crm || !setSelectedProperties || !checkDuplicateProperties) {
        return;
      }

      setSelectedProperties((prevProperties) => {
        const existingRow = findExistingRow(prevProperties, plot);

        if (existingRow) {
          selectExistingRow(existingRow.id, setCurrentPopupRowId);
          return prevProperties;
        }

        return addNewRowAndSelect(prevProperties, plot, setCurrentPopupRowId);
      });
    },
    [crm, setSelectedProperties, checkDuplicateProperties, setCurrentPopupRowId]
  );

  // 選択されたプロットのタグを取得する関数
  const getSelectedPlotTags = useCallback((): string[] => {
    if (!selectedPlotRef.current) return [];
    const properties = selectedPropertiesRef.current ?? [];
    const currentPlot = selectedPlotRef.current;

    const row = properties.find(
      (r) =>
        r.location === currentPlot.location &&
        r.chibanKaokuNumber === currentPlot.chiban &&
        r.prefName === currentPlot.prefectureName
    );

    return row?.tags ?? [];
  }, []);

  // 選択されたプロットの備考を取得する関数
  const getSelectedPlotRemarks = useCallback((): string => {
    if (!selectedPlotRef.current) return "";
    const properties = selectedPropertiesRef.current ?? [];
    const currentPlot = selectedPlotRef.current;

    const row = properties.find(
      (r) =>
        r.location === currentPlot.location &&
        r.chibanKaokuNumber === currentPlot.chiban &&
        r.prefName === currentPlot.prefectureName
    );

    return row?.remarks ?? "";
  }, []);

  // 選択されたプロットのタグ変更ハンドラー
  const handleSelectedPlotTagsChange = useCallback(
    (tags: string[]): void => {
      if (!selectedPlotRef.current || !onTagsChange) return;
      onTagsChange(
        tags,
        selectedPlotRef.current.prefectureName,
        selectedPlotRef.current.location,
        selectedPlotRef.current.chiban
      );
    },
    [onTagsChange]
  );

  // 選択されたプロットの備考変更ハンドラー
  const handleSelectedPlotRemarksChange = useCallback(
    (remarks: string): void => {
      if (!selectedPlotRef.current || !onRemarksChange) return;
      onRemarksChange(
        remarks,
        selectedPlotRef.current.prefectureName,
        selectedPlotRef.current.location,
        selectedPlotRef.current.chiban
      );
    },
    [onRemarksChange]
  );

  // 選択されたプロットの追加項目を取得する関数
  const getSelectedPlotAdditionalFields = useCallback((): string => {
    if (!selectedPlotRef.current) return "";
    const properties = selectedPropertiesRef.current ?? [];
    const currentPlot = selectedPlotRef.current;

    const row = properties.find(
      (r) =>
        r.location === currentPlot.location &&
        r.chibanKaokuNumber === currentPlot.chiban &&
        r.prefName === currentPlot.prefectureName
    );

    return row?.additionalFields ?? "";
  }, []);

  // 選択されたプロットの追加項目変更ハンドラー
  const handleSelectedPlotAdditionalFieldsChange = useCallback(
    (additionalFields: string): void => {
      if (!selectedPlotRef.current || !onAdditionalFieldsChange) return;
      onAdditionalFieldsChange(
        additionalFields,
        selectedPlotRef.current.prefectureName,
        selectedPlotRef.current.location,
        selectedPlotRef.current.chiban
      );
    },
    [onAdditionalFieldsChange]
  );

  useEffect(() => {
    if (!map) return;

    const handleZoomEnd = (): void => {
      setZoomLevel(map.getZoom());
    };

    if (typeof visibleProp === "boolean") {
      setVisible(visibleProp);
    }

    const handleOverlayAdd = (e: LayersControlEvent): void => {
      if (e.name === REQUESTED_PICTURES_PLOT_LAYER_NAME) {
        setVisible(true);
      }
    };

    const handleOverlayRemove = (e: LayersControlEvent): void => {
      if (e.name === REQUESTED_PICTURES_PLOT_LAYER_NAME) {
        setVisible(false);
        setSelectedPlot(null);
      }
    };

    map.on("zoomend", handleZoomEnd);
    if (typeof visibleProp !== "boolean") {
      map.on("overlayadd", handleOverlayAdd);
      map.on("overlayremove", handleOverlayRemove);
    }

    return () => {
      map.off("zoomend", handleZoomEnd);
      map.off("overlayadd", handleOverlayAdd);
      map.off("overlayremove", handleOverlayRemove);
    };
  }, [map, visibleProp]);

  useEffect(() => {
    if (typeof visibleProp === "boolean") {
      setVisible(visibleProp);
      if (!visibleProp) {
        setSelectedPlot(null);
      }
    }
  }, [visibleProp]);

  // 詳細ダイアログを開いている登記のピンを選択中にする（このピンはポップアップを持たないため）
  useEffect(() => {
    const selectedId = selectedPlot ? generatePlotId(selectedPlot) : null;
    markersRef.current.forEach((marker, plotId) => {
      marker.setSelected(plotId === selectedId);
    });
  }, [selectedPlot]);

  // マーカー作成と管理を行うuseEffect
  useEffect(() => {
    if (!map || !visible || !requestedPictures) return;

    // レイヤーグループが存在しない場合は作成
    if (!layerRef.current) {
      layerRef.current = L.layerGroup().addTo(map);
    }

    if (requestedPictures.length > 0) {
      // 現在のデータに基づくマーカーIDのセット（削除対象の特定に使用）
      const currentPlotIds = new Set<string>();

      // canvas は追加した順に重ねて描くため、L.Marker と同じく南のピンが上に来るよう北から並べる
      [...requestedPictures]
        .sort((a, b) => b.latitude - a.latitude)
        .forEach((plot) => {
          const plotId = generatePlotId(plot);
          currentPlotIds.add(plotId);

          // 既存のマーカーがキャッシュにあるかチェック
          // キャッシュがあれば再利用（二回クリック問題を防ぐ）
          let marker = markersRef.current.get(plotId);

          if (!marker) {
            // 新しいマーカーを作成
            marker = createRequestedPicturesPlotMarker(
              plot,
              handleMarkerClick,
              (selectedPlot) => {
                selectedPlotRef.current = selectedPlot;
                setSelectedPlot(selectedPlot);
              },
              getSharedCanvasPinRenderer(map),
              Boolean(crm)
            );

            // マーカーをキャッシュとレイヤーに追加
            markersRef.current.set(plotId, marker);
            layerRef.current?.addLayer(marker);
          }
        });

      // データから削除されたプロットのマーカーをクリーンアップ
      cleanupObsoleteMarkers(
        markersRef.current,
        currentPlotIds,
        layerRef.current
      );
    }
  }, [map, visible, requestedPictures, handleMarkerClick]);

  useEffect(() => {
    if (layerRef.current) {
      if (
        visible &&
        zoomLevel &&
        zoomLevel - 1 >= MIN_ZOOM &&
        zoomLevel - 1 <= MAX_ZOOM
      ) {
        layerRef.current.addTo(map);
      } else {
        map.removeLayer(layerRef.current);
        selectedPlotRef.current = null;
        setSelectedPlot(null);
      }
    }
  }, [zoomLevel, map, visible]);

  // 「取得済みの登記情報の情報を地図に反映しました」トーストが連続で表示されてしまう問題の対策
  useEffect(() => {
    if (isError) {
      toast.error("取得済みの登記情報の取得に失敗しました。");
      toastShownRef.current = false; // エラー時はリセット
    } else {
      if (
        !isLoading &&
        requestedPictures &&
        requestedPictures.length > 0 &&
        !toastShownRef.current
      ) {
        toast.success("取得済みの登記情報を地図に反映しました。");
        toastShownRef.current = true;
      } else if (!requestedPictures || requestedPictures.length === 0) {
        // データがクリアされたらリセット
        toastShownRef.current = false;
      }
    }
  }, [isLoading, requestedPictures, isError]);

  return (
    <>
      <FeatureGroup />
      <RealEstateDetailDialog
        plot={selectedPlot}
        onClose={() => {
          selectedPlotRef.current = null;
          setSelectedPlot(null);
        }}
        handlePinSave={crm ? handlePinSave : undefined}
        getInitialTags={crm ? getSelectedPlotTags : undefined}
        onTagsChange={crm ? handleSelectedPlotTagsChange : undefined}
        getInitialRemarks={crm ? getSelectedPlotRemarks : undefined}
        onRemarksChange={crm ? handleSelectedPlotRemarksChange : undefined}
        getInitialAdditionalFields={
          crm ? getSelectedPlotAdditionalFields : undefined
        }
        onAdditionalFieldsChange={
          crm ? handleSelectedPlotAdditionalFieldsChange : undefined
        }
        availableFields={availableFields}
        hasAvailableFields={hasAvailableFields}
        crm={!!crm}
      />
    </>
  );
};

/**
 * 取得済みの登記情報のプロットのマーカーを生成し、クリックイベントを追加する関数
 * @param plot 取得済みの登記情報のデータ
 * @param handleMarkerClick マーカークリックハンドラー
 * @param onSelectPlot プロットが選択された際に実行するコールバック
 * @param renderer 地図で共有する Canvas レンダラ（件数の上限が無いため DOM のマーカーにしない）
 * @param addsToList クリックで物件一覧に入るか（CRM）。入るなら選択中は一覧の青で描く
 * @return CanvasPinMarker
 */
const createRequestedPicturesPlotMarker = (
  plot: RequestedPicturesPlotDataResponse,
  handleMarkerClick: (plot: RequestedPicturesPlotDataResponse) => void,
  onSelectPlot: (selected: RequestedPicturesPlotDataResponse) => void,
  renderer: CanvasPinRenderer,
  addsToList: boolean
): CanvasPinMarker => {
  const marker = new CanvasPinMarker([plot.latitude, plot.longitude], {
    icon: getTeardropPinIcon(PIN_COLORS.requestedPicture),
    selectedIcon: getTeardropPinIcon(
      addsToList ? PIN_COLORS.listed : PIN_COLORS.requestedPicture,
      { selected: true }
    ),
    renderer,
  });

  marker.on("click", () => {
    // PropertySelectionGridに追加
    handleMarkerClick(plot);
    // ダイアログを開く
    onSelectPlot(plot);
  });
  return marker;
};
